import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  CSSProperties,
} from "react";
import { Icon } from "@material-ui/core";
import { OrderCompositionItem } from "domains/order/agreggates/order/OrderCompositionItem";
import styles from "./CompositionItem.module.scss";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import { hexToRGBA } from "utils/utils";
import { useTranslation } from "i18n/useTranslation";

interface CompositionItemProps {
  id: string;
  imageUrl: string;
  description: string;
  value: number;
  onQuantityChanged: (quantity: number, quantityFree?: number) => void;
  disabled?: boolean;
  max: number;
  quantityfree: number;
  disabledAdd?: boolean;
  initialQuantity?: number;
  type: "radio" | "checkbox" | "quantity" | "obsCheckBox";
  onSelect?: (id: string) => void;
  getTotalItemsFree: () => number;
  selectedItemsReplica: OrderCompositionItem[];
  itemType: number;
}

export interface ICompositionItemChecked {
  unCheck: () => void;
  check: () => void;
}

export enum CompositionTypeEnum {
  "Fixed" = 1,
  "Variable" = 2,
}

export const CompositionItem = forwardRef<
  ICompositionItemChecked,
  CompositionItemProps
>(
  (
    {
      id,
      selectedItemsReplica,
      onQuantityChanged,
      onSelect,
      quantityfree,
      max,
      type,
      imageUrl,
      description,
      value,
      disabledAdd,
      initialQuantity,
      itemType,
    },
    ref
  ) => {
    const { resolution } = useScreenStore();
    const { t } = useTranslation();
    const { kiosk } = useKiosk();
    const [quantity, setQuantity] = useState(initialQuantity ?? 0);

    useEffect(() => {
      let replicas = [...selectedItemsReplica];
      let innerItem = replicas.find((replica) => {
        return replica.compositionItemId === id;
      });

      if (quantity === 0 && innerItem !== undefined) {
        setQuantity(innerItem.quantity);
      }
    }, [id, quantity, selectedItemsReplica]);

    const addOne = useCallback(() => {
      setQuantity((prevQuantity) => prevQuantity + 1);
      onQuantityChanged(quantity + 1, quantityfree);
    }, [onQuantityChanged, quantity, quantityfree]);

    const removeOne = useCallback(() => {
      setQuantity((prevQuantity) => prevQuantity - 1);
      onQuantityChanged(quantity - 1, quantityfree);
    }, [onQuantityChanged, quantity, quantityfree]);

    const add = useCallback(() => {
      if (quantity < max) {
        addOne();
      }
    }, [addOne, max, quantity]);

    const remove = useCallback(() => {
      if (quantity > 0) {
        removeOne();
      }
    }, [quantity, removeOne]);

    const toggle = useCallback(() => {
      if (quantity > 0) {
        setQuantity(0);
        onQuantityChanged(0, 0);
      } else {
        setQuantity(1);
        onQuantityChanged(1, 1);
        if (onSelect) {
          onSelect(id);
        }
      }
    }, [id, onQuantityChanged, onSelect, quantity]);

    useImperativeHandle(ref, () => ({
      unCheck: () => {
        setQuantity(0);
      },
      check: () => {
        setQuantity(1);
      },
    }));

    const ifDisableAdd = useCallback(() => {
      if (disabledAdd) {
        return true;
      } else {
        if (max) {
          if (quantity >= max) {
            return true;
          } else {
            return false;
          }
        }
        return false;
      }
    }, [disabledAdd, max, quantity]);

    const getClass = useCallback(() => {
      if (type === "quantity") {
        return styles.quantity;
      }
      if (type === "checkbox") {
        return styles.checkbox;
      }
      if (type === "radio") {
        return styles.radio;
      }
      if (type === "obsCheckBox") {
        return styles.obsCheckBox;
      }
    }, [type]);

    const onImgError = (ev: React.ChangeEvent<HTMLImageElement>) => {
      ev.target.src = require("../../../../../assets/images/no-image.png");
    };

    return (
      <div
        onClick={type !== "quantity" ? toggle : undefined}
        className={getClass()}
        style={{
          border:
            (type === "checkbox" ||
              type === "radio" ||
              type === "obsCheckBox") &&
            quantity > 0
              ? `3px solid var(--primary)`
              : `3px solid #fbfbf9`,
          maxWidth: resolution === ResolutionType.FULL_HD ? 425 : 304,
        }}
      >
        {type === "quantity" && (
          <div className={styles.quantity}>
            <div
              className={styles.product}
              onClick={ifDisableAdd() ? undefined : add}
            >
              <img
                src={imageUrl}
                onErrorCapture={onImgError} 
                alt="produto"
                style={{
                  width: resolution === ResolutionType.FULL_HD ? 75 : 60,
                  height: resolution === ResolutionType.FULL_HD ? 75 : 60,
                }}
              />
              <div
                style={{
                  minHeight: resolution === ResolutionType.FULL_HD ? 110 : 75,
                }}
              >
                <span
                  style={{
                    fontSize: resolution === ResolutionType.FULL_HD ? 30 : 20,
                    marginBottom: resolution === ResolutionType.FULL_HD ? 8 : 6,
                  }}
                >
                  {description}
                </span>
                <span
                  style={{
                    fontSize: resolution === ResolutionType.FULL_HD ? 24 : 16,
                  }}
                >
                  {`R$ ${value.toFixed(2).replace(".", ",")}`}
                  {quantityfree > 0 ? (
                    <span
                      style={{
                        fontSize:
                          resolution === ResolutionType.FULL_HD ? 24 : 16,
                      }}
                    >
                      {t("cart_view.composition.free", {
                        quantity_free: quantityfree,
                      })}
                    </span>
                  ) : (
                    ""
                  )}
                </span>
              </div>
            </div>
            <div
              className={styles.quantitySelector}
              style={{
                height: resolution === ResolutionType.FULL_HD ? 60 : 50,
              }}
            >
              <div
                style={
                  {
                    width: resolution === ResolutionType.FULL_HD ? 200 : 160,
                    "--color": hexToRGBA(kiosk?.themeColor ?? kiosk?.primaryColor ?? "#001537", 0.1),
                  } as CSSProperties
                }
              >
                <button
                  disabled={
                    itemType === CompositionTypeEnum.Fixed || quantity <= 0
                  }
                  onClick={remove}
                >
                  <Icon
                    style={{
                      opacity:
                        itemType === CompositionTypeEnum.Fixed || quantity <= 0
                          ? 0
                          : 1,
                    }}
                  >
                    remove
                  </Icon>
                </button>
                <span>{quantity}</span>
                <button
                  disabled={
                    itemType === CompositionTypeEnum.Fixed || ifDisableAdd()
                  }
                  onClick={add}
                >
                  <Icon
                    style={{
                      opacity:
                        itemType === CompositionTypeEnum.Fixed || ifDisableAdd()
                          ? 0
                          : 1,
                    }}
                  >
                    add
                  </Icon>
                </button>
              </div>
            </div>
          </div>
        )}

        {type === "checkbox" && (
          <div className={styles.checkbox}>
            <img
              src={imageUrl}
              alt="produto"
              onErrorCapture={onImgError} 
              style={{
                width: resolution === ResolutionType.FULL_HD ? 75 : 60,
                height: resolution === ResolutionType.FULL_HD ? 75 : 60,
              }}
            />
            <div
              className={styles.product}
              style={{
                minHeight: resolution === ResolutionType.FULL_HD ? 110 : 75,
              }}
            >
              <span
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 30 : 20,
                  marginBottom: resolution === ResolutionType.FULL_HD ? 8 : 6,
                }}
              >
                {description}
              </span>
              <span
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 24 : 16,
                }}
              >{`R$ ${value.toFixed(2).replace(".", ",")}`}</span>
            </div>
          </div>
        )}

        {type === "radio" && (
          <div
            className={styles.radio}
            style={{
              height: resolution === ResolutionType.FULL_HD ? 100 : 80,
            }}
          >
            <span
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 30 : 24,
              }}
            >
              {description}
            </span>
          </div>
        )}

        {type === "obsCheckBox" && (
          <div
            className={styles.radio}
            style={{
              height: resolution === ResolutionType.FULL_HD ? 100 : 80,
            }}
          >
            <span
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 30 : 24,
              }}
            >
              {description}
            </span>
          </div>
        )}
      </div>
    );
  }
);
